import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { authApi, shopApi, setOnUnauthorized } from '../api/client';
import { SecureStorageService } from '../services/SecureStorageService';
import { NotificationService } from '../services/NotificationService';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [userLocation, setUserLocation] = useState(() => {
    try {
      const saved = localStorage.getItem('user_location');
      return saved ? JSON.parse(saved) : { lat: 28.1515, lng: 77.3417 };
    } catch {
      return { lat: 28.1515, lng: 77.3417 };
    }
  });

  const refreshLocation = useCallback(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          const loc = { lat: pos.coords.latitude, lng: pos.coords.longitude };
          setUserLocation(loc);
          try {
            localStorage.setItem('user_location', JSON.stringify(loc));
          } catch {}
        },
        (err) => {
          console.log('Location access denied/unavailable, using fallback:', err.message);
          setUserLocation(prev => prev || { lat: 28.1515, lng: 77.3417 });
        },
        { enableHighAccuracy: true, timeout: 10000 }
      );
    } else {
      setUserLocation(prev => prev || { lat: 28.1515, lng: 77.3417 });
    }
  }, []);

  const continueAsGuest = useCallback(() => {
    const guestUser = {
      isGuest: true,
      id: '00000000-0000-0000-0000-000000000000',
      name: 'Guest',
      mobile: '0000000000',
      isShopOwner: false
    };
    setUser(guestUser);
    refreshLocation();
    return guestUser;
  }, [refreshLocation]);

  useEffect(() => {
    setOnUnauthorized(() => {
      setUser(null);
      continueAsGuest();
    });
    refreshLocation();
  }, [refreshLocation, continueAsGuest]);

  const syncPreferencesToLocalStorage = (userData, shopData) => {
    if (userData) {
      localStorage.setItem('user_setting_allow_calls', userData.allowCalls !== false ? 'true' : 'false');
      localStorage.setItem('user_setting_mute_chat_alerts', userData.muteChatAlerts ? 'true' : 'false');
      localStorage.setItem('muted_chat_ids', userData.mutedChatIdsJson || '[]');
    }
    if (shopData) {
      localStorage.setItem('shop_setting_disable_calls', shopData.disableShopCalls ? 'true' : 'false');
      localStorage.setItem('shop_setting_mute_notifications', shopData.muteShopNotifications ? 'true' : 'false');
    }
  };

  const clearPreferencesFromLocalStorage = () => {
    localStorage.removeItem('user_setting_allow_calls');
    localStorage.removeItem('user_setting_mute_chat_alerts');
    localStorage.removeItem('muted_chat_ids');
    localStorage.removeItem('shop_setting_disable_calls');
    localStorage.removeItem('shop_setting_mute_notifications');
  };

  useEffect(() => {
    const initAuth = async () => {
      const stored = await SecureStorageService.getUserInfo();
      const token = await SecureStorageService.getAccessToken();
      if (stored && token) {
        let userData = stored;
        let shopData = null;
        if (userData.isShopOwner && !userData.shopId) {
          try {
            const shopsRes = await shopApi.myShops();
            const shops = shopsRes.data.data;
            if (shops && shops.length > 0) {
              shopData = shops[0];
              userData.shopId = shops[0].id;
              userData.shopSlug = shops[0].slug;
              userData.shopLogo = shops[0].logo;
              await SecureStorageService.setUserInfo(userData);
            }
          } catch (err) {
            console.error("Failed to fetch shop details during init:", err);
          }
        }
        setUser(userData);
        syncPreferencesToLocalStorage(userData, shopData);
        NotificationService.registerDeviceToken();
      } else {
        continueAsGuest();
      }
      setLoading(false);
    };
    initAuth();
  }, []);

  const login = useCallback(async (mobile, password) => {
    const res = await authApi.login({ mobile, password });
    const data = res.data.data;
    await SecureStorageService.setAccessToken(data.accessToken);
    await SecureStorageService.setRefreshToken(data.refreshToken);
    let shopData = null;
    if (data.isShopOwner) {
      try {
        const shopsRes = await shopApi.myShops();
        const shops = shopsRes.data.data;
        if (shops && shops.length > 0) {
          shopData = shops[0];
          data.shopId = shops[0].id;
          data.shopSlug = shops[0].slug;
          data.shopLogo = shops[0].logo;
        }
      } catch (err) {
        console.error("Failed to fetch shop details on login:", err);
      }
    }
    await SecureStorageService.setUserInfo(data);
    setUser(data);
    syncPreferencesToLocalStorage(data, shopData);
    refreshLocation();
    NotificationService.registerDeviceToken();
    return data;
  }, [refreshLocation]);

  const getGeoLocationDetails = async () => {
    let loc = userLocation;
    if (!loc || (!loc.lat && !loc.lng)) {
      try {
        const saved = localStorage.getItem('user_location');
        if (saved) loc = JSON.parse(saved);
      } catch {}
    }
    const latitude = loc?.lat || null;
    const longitude = loc?.lng || null;
    let address = null;
    if (latitude && longitude) {
      try {
        const res = await fetch(`https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}`, {
          headers: { 'Accept-Language': 'en' },
          signal: AbortSignal.timeout(3000)
        });
        if (res.ok) {
          const geoData = await res.json();
          address = geoData.display_name || null;
        }
      } catch {
        address = `Lat: ${latitude.toFixed(4)}, Lng: ${longitude.toFixed(4)}`;
      }
    }
    return { latitude, longitude, address };
  };

  const register = useCallback(async (name, mobile, password, email, slug) => {
    const geo = await getGeoLocationDetails();
    const res = await authApi.register({ 
      name, mobile, password, email, slug,
      latitude: geo.latitude,
      longitude: geo.longitude,
      address: geo.address
    });
    const data = res.data.data;
    await SecureStorageService.setAccessToken(data.accessToken);
    await SecureStorageService.setRefreshToken(data.refreshToken);
    await SecureStorageService.setUserInfo(data);
    setUser(data);
    syncPreferencesToLocalStorage(data, null);
    refreshLocation();
    NotificationService.registerDeviceToken();
    return data;
  }, [refreshLocation, userLocation]);

  const logout = useCallback(async () => {
    await NotificationService.unregister();
    try { await authApi.logout(); } catch {}
    await SecureStorageService.clearAll();
    clearPreferencesFromLocalStorage();
    setUser(null);
  }, []);

  const updateUser = useCallback((updates) => {
    setUser(prev => {
      if (!prev) return null;
      const updated = { ...prev, ...updates };
      SecureStorageService.setUserInfo(updated);
      return updated;
    });
  }, []);

  const [authModal, setAuthModal] = useState({ isOpen: false, actionName: '' });

  const openAuthModal = useCallback((actionName = 'access this feature') => {
    setAuthModal({ isOpen: true, actionName });
  }, []);

  const closeAuthModal = useCallback(() => {
    setAuthModal({ isOpen: false, actionName: '' });
  }, []);

  const requireAuth = useCallback((actionName = 'access this feature') => {
    if (!user || user.isGuest) {
      setAuthModal({ isOpen: true, actionName });
      return false;
    }
    return true;
  }, [user]);

  const loginWithGoogle = useCallback(async (credential) => {
    const geo = await getGeoLocationDetails();
    const res = await authApi.googleLogin(credential, geo);
    const data = res.data.data;
    await SecureStorageService.setAccessToken(data.accessToken);
    await SecureStorageService.setRefreshToken(data.refreshToken);
    let shopData = null;
    if (data.isShopOwner) {
      try {
        const shopsRes = await shopApi.myShops();
        const shops = shopsRes.data.data;
        if (shops && shops.length > 0) {
          shopData = shops[0];
          data.shopId = shops[0].id;
          data.shopSlug = shops[0].slug;
          data.shopLogo = shops[0].logo;
        }
      } catch (err) {
        console.error("Failed to fetch shop details on Google login:", err);
      }
    }
    await SecureStorageService.setUserInfo(data);
    setUser(data);
    syncPreferencesToLocalStorage(data, shopData);
    refreshLocation();
    NotificationService.registerDeviceToken();
    return data;
  }, [refreshLocation, userLocation]);

  const isAuth = !!user && !user.isGuest;
  const isGuest = !user || user.isGuest;

  return (
    <AuthContext.Provider value={{ 
      user, loading, login, register, loginWithGoogle, logout, updateUser, continueAsGuest, 
      isAuth, isGuest, userLocation, refreshLocation,
      authModal, openAuthModal, closeAuthModal, requireAuth
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
};
